// Funções de cálculo e status de certidões para o Dashboard Executivo
import { resolveChipColorFromThresholds } from './dashboardChips';

const resolvePropertiesRoot = (data) => {
  if (!data || typeof data !== 'object') return {};

  if (data.gn_properties && typeof data.gn_properties === 'object') return data.gn_properties;
  if (data.properties && typeof data.properties === 'object') return data.properties;

  if (data.data && typeof data.data === 'object') {
    if (data.data.gn_properties && typeof data.data.gn_properties === 'object')
      return data.data.gn_properties;
    if (data.data.properties && typeof data.data.properties === 'object')
      return data.data.properties;
  }

  return {};
};

const normalizeObjectArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object');
  if (typeof value === 'object') return [value];
  return [];
};

const extractCndSituacoes = (record) => {
  const situacoes = [];

  const append = (value) => {
    if (value === null || value === undefined) return;
    const text = String(value).trim();
    if (text) situacoes.push(text);
  };

  // Formatos diretos/legados
  append(record?.situacao);

  const metadata = normalizeObjectArray(record?.metadata);
  metadata.forEach((m) => append(m?.situacao));

  const dataNode = normalizeObjectArray(record?.data);
  dataNode.forEach((d) => {
    append(d?.situacao);
    const dadosCnd = normalizeObjectArray(d?.dados_cnd);
    dadosCnd.forEach((dc) => {
      append(dc?.situacao);
      const estruturados = normalizeObjectArray(dc?.dados_estruturados);
      estruturados.forEach((e) => append(e?.situacao));

      const certidoes = normalizeObjectArray(dc?.certidao);
      certidoes.forEach((c) => append(c?.situacao));

      const dcMetadata = normalizeObjectArray(dc?.metadata);
      dcMetadata.forEach((m) => append(m?.situacao));
    });
  });

  // Novo agregado observado: imovel_rural -> contribuinte -> dados_cnd[] -> certidao[]/metadata[]
  const imoveis = normalizeObjectArray(record?.imovel_rural);
  imoveis.forEach((imovel) => {
    const contribuintes = normalizeObjectArray(imovel?.contribuinte);
    contribuintes.forEach((contribuinte) => {
      const dadosCnd = normalizeObjectArray(contribuinte?.dados_cnd);
      dadosCnd.forEach((dc) => {
        append(dc?.situacao);

        const certidoes = normalizeObjectArray(dc?.certidao);
        certidoes.forEach((c) => append(c?.situacao));

        const dcMetadata = normalizeObjectArray(dc?.metadata);
        dcMetadata.forEach((m) => append(m?.situacao));
      });

      const contribMetadata = normalizeObjectArray(contribuinte?.metadata);
      contribMetadata.forEach((m) => append(m?.situacao));
    });
  });

  return situacoes;
};

export function getCcirsDashboardStatus(data, dashboardChipsConfig) {
  const properties = resolvePropertiesRoot(data);

  const raw = properties.serpro_ccir;

  const records = (() => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.filter((item) => item && typeof item === 'object');
    }
    if (typeof raw === 'object') {
      return [raw];
    }
    return [];
  })();

  const label = 'CCIR';
  const count = records.length;

  if (count === 0) {
    let baseColor = 'error';

    const chipsConfig = dashboardChipsConfig || {};
    const certCfg = chipsConfig.certidoes || {};
    const ccirCfg = certCfg.ccir;

    if (ccirCfg && Array.isArray(ccirCfg.thresholds)) {
      baseColor = resolveChipColorFromThresholds(ccirCfg, count, baseColor) || baseColor;
    }

    return { label, color: baseColor, count };
  }

  if (count > 1) {
    let baseColor = 'warning';

    const chipsConfig = dashboardChipsConfig || {};
    const certCfg = chipsConfig.certidoes || {};
    const ccirCfg = certCfg.ccir;

    if (ccirCfg && Array.isArray(ccirCfg.thresholds)) {
      baseColor = resolveChipColorFromThresholds(ccirCfg, count, baseColor) || baseColor;
    }

    return { label, color: baseColor, count };
  }

  const ccir = records[0] || {};
  const dadosUltimoCcir =
    ccir && typeof ccir.dados_ultimo_ccir === 'object' ? ccir.dados_ultimo_ccir : null;

  const situacaoRaw = dadosUltimoCcir?.situacao_ccir;

  let color = 'warning';

  if (situacaoRaw !== null && situacaoRaw !== undefined && situacaoRaw !== '') {
    const normalized = String(situacaoRaw).trim().toLowerCase();
    if (normalized === 'quitado') {
      color = 'success';
    }
  }

  const chipsConfig = dashboardChipsConfig || {};
  const certCfg = chipsConfig.certidoes || {};
  const ccirCfg = certCfg.ccir;

  if (ccirCfg && Array.isArray(ccirCfg.thresholds)) {
    color = resolveChipColorFromThresholds(ccirCfg, count, color) || color;
  }

  return { label, color, count };
}

export function getCndDashboardStatus(data, dashboardChipsConfig) {
  const properties = resolvePropertiesRoot(data);

  const raw = properties.serpro_cnd;

  const records = (() => {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw.filter((item) => item && typeof item === 'object');
    }
    if (typeof raw === 'object') {
      return [raw];
    }
    return [];
  })();

  const label = 'CND';
  const count = records.length;

  if (count === 0) {
    let baseColor = 'error';

    const chipsConfig = dashboardChipsConfig || {};
    const certCfg = chipsConfig.certidoes || {};
    const cndCfg = certCfg.cnd;

    if (cndCfg && Array.isArray(cndCfg.thresholds)) {
      baseColor = resolveChipColorFromThresholds(cndCfg, count, baseColor) || baseColor;
    }

    return { label, color: baseColor, count };
  }

  if (count > 1) {
    let baseColor = 'warning';

    const chipsConfig = dashboardChipsConfig || {};
    const certCfg = chipsConfig.certidoes || {};
    const cndCfg = certCfg.cnd;

    if (cndCfg && Array.isArray(cndCfg.thresholds)) {
      baseColor = resolveChipColorFromThresholds(cndCfg, count, baseColor) || baseColor;
    }

    return { label, color: baseColor, count };
  }

  const cnd = records[0] || {};
  const situacoes = extractCndSituacoes(cnd);
  const situacaoRaw = situacoes.length > 0 ? situacoes[0] : null;

  let color = 'warning';

  if (situacaoRaw !== null && situacaoRaw !== undefined && situacaoRaw !== '') {
    const normalized = String(situacaoRaw).trim().toLowerCase();
    if (normalized.includes('negativa')) {
      color = 'success';
    }
  }

  const chipsConfig = dashboardChipsConfig || {};
  const certCfg = chipsConfig.certidoes || {};
  const cndCfg = certCfg.cnd;

  if (cndCfg && Array.isArray(cndCfg.thresholds)) {
    color = resolveChipColorFromThresholds(cndCfg, count, color) || color;
  }

  return { label, color, count };
}

export function getIbamaCertidaoStatusFromDocs(docsFiles, dashboardChipsConfig) {
  const files = Array.isArray(docsFiles) ? docsFiles : [];

  let count = 0;

  files.forEach((name) => {
    if (!name) return;
    const lower = String(name).toLowerCase();
    if (lower.includes('ibama') && lower.endsWith('.pdf')) {
      count += 1;
    }
  });

  // eslint-disable-next-line no-useless-assignment
  let color = 'warning';

  if (count === 0) {
    color = 'error';
  } else if (count < 3) {
    color = 'warning';
  } else {
    color = 'success';
  }

  const chipsConfig = dashboardChipsConfig || {};
  const certCfg = chipsConfig.certidoes || {};
  const ibamaCfg = certCfg.ibama;

  if (ibamaCfg && Array.isArray(ibamaCfg.thresholds)) {
    color = resolveChipColorFromThresholds(ibamaCfg, count, color) || color;
  }

  return {
    label: 'IBAMA',
    color,
    count,
  };
}
